import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const emptyForm = (siteId, email) => ({ site_id: siteId || '', recipient_email: email || '', report_type: 'daily', frequency: 'daily', send_time: '07:10', weekdays: [1], month_day: 1, interval_days: 2, anchor_date: new Date().toISOString().slice(0, 10), period_type: 'previous_day', period_days: 7, shift_id: '', include_open: true, active: true });
const frequencyLabel = (schedule) => {
  if (schedule.frequency === 'daily') return 'Todos los días';
  if (schedule.frequency === 'monthly') return `Cada mes, día ${schedule.month_day}`;
  if (schedule.frequency === 'interval') return `Cada ${schedule.interval_days} días`;
  return (schedule.weekdays || []).map((day) => DAYS[day - 1]).join(', ');
};
const periodLabel = (schedule) => {
  if (schedule.period_type === 'previous_week') return 'semana anterior';
  if (schedule.period_type === 'previous_month') return 'mes anterior';
  if (schedule.period_type === 'trailing_days') return `últimos ${schedule.period_days} días`;
  return 'día anterior';
};

const ScheduledReports = () => {
  const { user } = useAuth();
  const { currentSite, sites, canChangeSite } = useSite();
  const [catalog, setCatalog] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [email, setEmail] = useState('');
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [shifts, setShifts] = useState([]);
  const [form, setForm] = useState(emptyForm(currentSite?.id, user?.email));
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const eligible = user?.email && user?.user_type !== 'employee' && !user?.employee_number;
  const canChooseRecipient = ['admin_site', 'admin_master'].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/scheduled-reports');
      setCatalog(data.report_types);
      setSchedules(data.schedules);
      setEmail(data.email);
      setDeliveryEnabled(Boolean(data.delivery_enabled));
    } catch (error) {
      toast.error(error.response?.data?.message || 'No se pudieron cargar tus programaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (eligible) load(); }, [eligible]);
  useEffect(() => {
    if (!editingId && currentSite?.id) setForm((previous) => ({ ...previous, site_id: previous.site_id || currentSite.id }));
  }, [currentSite?.id, editingId]);
  useEffect(() => {
    if (!form.site_id) return;
    axios.get('/catalogs/all', { params: { site_id: form.site_id } })
      .then(({ data }) => setShifts(data.shifts || []))
      .catch(() => setShifts([]));
  }, [form.site_id]);

  const update = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));
  const chooseReport = (type) => setForm((previous) => ({
    ...previous,
    report_type: type,
    period_type: ['daily', 'labor_ax'].includes(type) ? 'previous_day' : type === 'weekly' ? 'previous_week' : previous.period_type,
  }));
  const reset = () => { setEditingId(null); setForm(emptyForm(currentSite?.id, email || user?.email)); };
  const edit = (schedule) => {
    setEditingId(schedule.id);
    setForm({
      site_id: schedule.site_id,
      recipient_email: schedule.recipient_email || email || user?.email || '',
      report_type: schedule.report_type,
      frequency: schedule.frequency,
      send_time: String(schedule.send_time).slice(0, 5),
      weekdays: schedule.weekdays || [1],
      month_day: schedule.month_day || 1,
      interval_days: schedule.interval_days || 2,
      anchor_date: schedule.anchor_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
      period_type: schedule.period_type,
      period_days: schedule.period_days || 7,
      shift_id: schedule.shift_id || '',
      include_open: Boolean(schedule.include_open),
      active: Boolean(schedule.active),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, site_id: Number(form.site_id), weekdays: form.frequency === 'weekdays' ? form.weekdays.map(Number) : null, month_day: form.frequency === 'monthly' ? Number(form.month_day) : null, interval_days: form.frequency === 'interval' ? Number(form.interval_days) : null, anchor_date: form.frequency === 'interval' ? form.anchor_date : null, period_days: form.period_type === 'trailing_days' ? Number(form.period_days) : null, shift_id: form.report_type === 'labor_ax' && form.shift_id ? Number(form.shift_id) : null };
      if (editingId) await axios.put(`/scheduled-reports/${editingId}`, payload);
      else await axios.post('/scheduled-reports', payload);
      toast.success(editingId ? 'Programación actualizada' : 'Reporte programado');
      reset();
      await load();
    } catch (error) {
      const firstError = Object.values(error.response?.data?.errors || {})[0]?.[0];
      toast.error(firstError || error.response?.data?.message || 'No se pudo guardar la programación');
    } finally { setSaving(false); }
  };
  const remove = async (schedule) => {
    if (!window.confirm(`¿Eliminar tu programación de ${catalog.find((item) => item.key === schedule.report_type)?.title || 'este reporte'}?`)) return;
    try {
      await axios.delete(`/scheduled-reports/${schedule.id}`);
      toast.success('Programación eliminada');
      if (editingId === schedule.id) reset();
      await load();
    } catch (error) { toast.error('No se pudo eliminar la programación'); }
  };

  if (!eligible) return <div className="p-6 text-slate-600">Solo usuarios de sistema con correo electrónico pueden configurar reportes programados.</div>;

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-6">
      <div>
        <h1 className="text-3xl font-extrabold text-blue-900">Reportes Programados</h1>
        <p className="mt-1 text-slate-600">Elige el PDF y cuándo enviarlo{canChooseRecipient ? ', incluido el correo destinatario.' : '.'}</p>
      </div>
      {!deliveryEnabled && <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">Entorno local: puedes configurar y probar la pantalla, pero no se enviarán correos.</p>}

      <form onSubmit={save} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-xl font-bold text-slate-900">{editingId ? 'Editar programación' : 'Nueva programación'}</h2>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <label className="text-sm font-semibold text-slate-700">Reporte
            <select aria-label="Reporte" value={form.report_type} onChange={(event) => chooseReport(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2">
              {catalog.map((report) => <option key={report.key} value={report.key}>{report.title}</option>)}
            </select>
          </label>
          {canChooseRecipient && <label className="text-sm font-semibold text-slate-700">Correo destinatario
            <input aria-label="Correo destinatario" type="email" required maxLength={255} value={form.recipient_email} onChange={(event) => update('recipient_email', event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
          </label>}
          <label className="text-sm font-semibold text-slate-700">Sitio
            <select aria-label="Sitio" value={form.site_id} onChange={(event) => update('site_id', event.target.value)} disabled={!canChangeSite()} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-100">
              <option value="">Selecciona sitio</option>
              {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">Frecuencia
            <select aria-label="Frecuencia" value={form.frequency} onChange={(event) => update('frequency', event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2">
              <option value="daily">Todos los días</option>
              <option value="weekdays">Días de semana elegidos</option>
              <option value="monthly">Una vez al mes</option>
              <option value="interval">Cada N días</option>
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">Hora local del sitio
            <input aria-label="Hora local del sitio" type="time" required value={form.send_time} onChange={(event) => update('send_time', event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
          </label>
          {form.frequency === 'monthly' && <label className="text-sm font-semibold text-slate-700">Día del mes
            <input aria-label="Día del mes" type="number" min="1" max="31" value={form.month_day} onChange={(event) => update('month_day', event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
          </label>}
          {form.frequency === 'interval' && <><label className="text-sm font-semibold text-slate-700">Cada cuántos días
            <input aria-label="Cada cuántos días" type="number" min="2" max="365" value={form.interval_days} onChange={(event) => update('interval_days', event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
          </label><label className="text-sm font-semibold text-slate-700">Fecha de inicio
            <input aria-label="Fecha de inicio" type="date" value={form.anchor_date} onChange={(event) => update('anchor_date', event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
          </label></>}
          {!['daily', 'labor_ax'].includes(form.report_type) && <label className="text-sm font-semibold text-slate-700">Periodo del PDF
            <select aria-label="Periodo del PDF" value={form.period_type} onChange={(event) => update('period_type', event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2">
              <option value="previous_day">Día anterior</option><option value="previous_week">Semana anterior (lunes a domingo)</option><option value="previous_month">Mes anterior</option><option value="trailing_days">Últimos N días completos</option>
            </select>
          </label>}
          {form.period_type === 'trailing_days' && <label className="text-sm font-semibold text-slate-700">Días del PDF
            <input aria-label="Días del PDF" type="number" min="2" value={form.period_days} onChange={(event) => update('period_days', event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
          </label>}
          {form.report_type === 'labor_ax' && <label className="text-sm font-semibold text-slate-700">Turno
            <select aria-label="Turno" value={form.shift_id} onChange={(event) => update('shift_id', event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2">
              <option value="">Todos los turnos</option>
              {shifts.map((shift) => <option key={shift.id} value={shift.id}>{shift.code} · {shift.name}</option>)}
            </select>
          </label>}
        </div>
        {form.frequency === 'weekdays' && <fieldset className="flex flex-wrap gap-2"><legend className="mb-2 text-sm font-semibold text-slate-700">Días de envío</legend>{DAYS.map((day, index) => <label key={day} className="flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm"><input type="checkbox" checked={form.weekdays.includes(index + 1)} onChange={(event) => setForm((previous) => ({ ...previous, weekdays: event.target.checked ? [...previous.weekdays, index + 1].sort() : previous.weekdays.filter((value) => value !== index + 1) }))} />{day}</label>)}</fieldset>}
        <div className="flex flex-wrap gap-5 text-sm font-semibold text-slate-700">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.include_open} onChange={(event) => update('include_open', event.target.checked)} />Incluir reportes abiertos</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(event) => update('active', event.target.checked)} />Activada</label>
        </div>
        <p className="text-xs text-slate-500">El horario usa la zona horaria del sitio. Para días inexistentes (por ejemplo, 31 de febrero), el envío se hará el último día del mes.</p>
        <div className="flex gap-3"><button disabled={saving || loading} className="rounded-lg bg-blue-700 px-5 py-2 font-bold text-white disabled:bg-slate-400">{saving ? 'Guardando…' : editingId ? 'Guardar cambios' : 'Programar reporte'}</button>{editingId && <button type="button" onClick={reset} className="rounded-lg border border-slate-300 px-5 py-2">Cancelar</button>}</div>
      </form>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-1 text-xl font-bold text-slate-900">Mis programaciones</h2>
        <p className="mb-4 text-sm text-slate-600">Incluye las que creaste y las que el administrador asignó a tu correo. Puedes editarlas o eliminarlas.</p>
        {loading ? <p className="text-slate-500">Cargando…</p> : schedules.length === 0 ? <p className="text-slate-500">Todavía no tienes reportes programados.</p> : <div className="space-y-3">{schedules.map((schedule) => {
          const last = schedule.deliveries?.[0];
          return <div key={schedule.id} className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 p-4">
            <div><div className="flex items-center gap-2"><strong className="text-slate-900">{catalog.find((item) => item.key === schedule.report_type)?.title || schedule.report_type}</strong><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${schedule.active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>{schedule.active ? 'Activa' : 'Pausada'}</span>{schedule.assigned_by_admin && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">Asignada por administrador</span>}</div>
              <div className="mt-1 text-sm text-slate-600">Para {schedule.recipient_email || email || user.email} · {schedule.site?.name} · {frequencyLabel(schedule)} a las {String(schedule.send_time).slice(0, 5)} ({schedule.site?.timezone}) · PDF del {periodLabel(schedule)}</div>
              {last && <div className="mt-1 text-xs text-slate-500">Último intento: {last.period_start} al {last.period_end} · {last.status === 'sent' ? 'Enviado' : last.status === 'failed' ? 'Falló' : 'En proceso'}</div>}
            </div>
            <div className="flex gap-2"><button type="button" onClick={() => edit(schedule)} className="rounded-lg border border-blue-300 px-3 py-1.5 font-semibold text-blue-700">Editar</button><button type="button" onClick={() => remove(schedule)} className="rounded-lg border border-red-200 px-3 py-1.5 font-semibold text-red-700">Eliminar</button></div>
          </div>;
        })}</div>}
      </section>
    </div>
  );
};

export default ScheduledReports;
